import { CheckboxEditor, DropdownEditor, Field } from '@approvalmax/ui';
import { RichEditor } from '@approvalmax/ui/src/components';
import { constants } from 'modules/common';
import { PlainDataProvider } from 'modules/data-providers';
import { AlertNotificationIcon } from 'modules/sprites';
import { AttachmentKind } from 'pages/requestForm/data/AttachmentKind';
import { FC, memo, useMemo } from 'react';
import { useIsEmailContactLimited } from 'shared/hooks';

import { getValues } from '../../EmailToSupplierForm.helpers';
import { useEmailToSupplierFormQa, useFormHandlers, useValidations } from '../../EmailToSupplierForm.hooks';
import {
    CheckboxWrap,
    Content,
    ContentFrom,
    ContentReplyTo,
    FieldsColumnLeft,
    FieldsColumnRight,
    FieldsFullWidth,
    FieldsWrap,
    FilesList,
    FilesNote,
    FilesWrap,
    FormWrap,
    NoteWrap,
} from '../../EmailToSupplierForm.styles';
import ContactListItem from '../ContactListItem/ContactListItem';
import { ContactListItemProps } from '../ContactListItem/ContactListItem.types';
import { FormOldProps } from './FormOld.types';

export const FormOld = memo<FormOldProps>((props) => {
    const {
        data,
        onChangeData,
        labels,
        onVisibilityToggle,
        isReadonly,
        toOptionsFilterFunction,
        ccOptionsFilterFunction,
        attachments,
        attachmentsNew,
        onAttachRemove,
        isVisible,
        hasErrors,
        reference,
        hideCheckbox,
        integrationType,
        getMessage,
        emailOptions,
        customMentionConfig,
        body,
        subject,
        handleClickOnAttachment,
        toCreateValidatorFunction,
        toValidatorFunction,
        ccCreateValidatorFunction,
        ccValidatorFunction,
    } = props;

    const { qaForm, qaFormToggle, qaTo, qaCc, qaSubject, qaBody } = useEmailToSupplierFormQa(integrationType);

    const isEmailContactLimited = useIsEmailContactLimited();
    const isDisabled = isReadonly || isEmailContactLimited;

    const { onChangeBody, onChangeSubject, onChangeTo, onChangeCc, onCreateEmail } = useFormHandlers(
        data,
        onChangeData
    );

    const { handleToValidate, handleCcValidate, handleToCreateValidate, handleCcCreateValidate } = useValidations({
        toCreateValidatorFunction,
        toValidatorFunction,
        ccCreateValidatorFunction,
        ccValidatorFunction,
    });

    const ListItem = useMemo<FC<ContactListItemProps>>(
        () => (props) => <ContactListItem {...props} integrationType={integrationType} />,
        [integrationType]
    );

    return (
        <FormWrap title={data && isVisible ? getMessage('formTitle') : undefined} qa={qaForm}>
            <Content ref={reference}>
                {!isReadonly && !hideCheckbox && (
                    <CheckboxWrap $isEmailContactLimited={isEmailContactLimited}>
                        <CheckboxEditor qa={qaFormToggle} value={isVisible} onChange={onVisibilityToggle}>
                            {getMessage('toggleLabel')}
                        </CheckboxEditor>
                    </CheckboxWrap>
                )}

                {isEmailContactLimited && (
                    <NoteWrap>
                        <AlertNotificationIcon />

                        <FilesNote>{getMessage('sectionNote')}</FilesNote>
                    </NoteWrap>
                )}

                {data && isVisible && (
                    <FieldsWrap>
                        <FieldsColumnLeft>
                            <Field required title={getMessage('toLabel')}>
                                <PlainDataProvider items={emailOptions} filterFn={toOptionsFilterFunction}>
                                    <DropdownEditor
                                        qa={qaTo}
                                        multiple
                                        invalid={hasErrors && !handleToValidate(data)}
                                        value={getValues(emailOptions, data.to)}
                                        onChange={onChangeTo}
                                        placeholder={getMessage('toPlaceholder')}
                                        emptyListPlaceholder={getMessage('toPlaceholderEmptyList')}
                                        canCreateItemFn={handleToCreateValidate}
                                        onCreateItem={onCreateEmail}
                                        listItem={ListItem}
                                        disabled={isDisabled}
                                    />
                                </PlainDataProvider>
                            </Field>

                            <Field title={getMessage('ccLabel')}>
                                <PlainDataProvider items={emailOptions} filterFn={ccOptionsFilterFunction}>
                                    <DropdownEditor
                                        qa={qaCc}
                                        multiple
                                        invalid={hasErrors && !handleCcValidate(data)}
                                        value={getValues(emailOptions, data.cc)}
                                        onChange={onChangeCc}
                                        placeholder={getMessage('ccPlaceholder')}
                                        emptyListPlaceholder={getMessage('ccPlaceholderEmptyList')}
                                        canCreateItemFn={handleCcCreateValidate}
                                        onCreateItem={onCreateEmail}
                                        listItem={ListItem}
                                        disabled={isDisabled}
                                    />
                                </PlainDataProvider>
                            </Field>
                        </FieldsColumnLeft>

                        <FieldsColumnRight>
                            <Field title={getMessage('fromLabel')}>
                                <ContentFrom>{data.from}</ContentFrom>
                            </Field>

                            <Field title={getMessage('replyToLabel')}>
                                <ContentReplyTo>{data.replyTo}</ContentReplyTo>
                            </Field>
                        </FieldsColumnRight>

                        <FieldsFullWidth>
                            <Field title={getMessage('subjectLabel')}>
                                <RichEditor
                                    dataQa={qaSubject}
                                    value={subject || ''}
                                    onChange={onChangeSubject}
                                    invalid={hasErrors && !data.subject}
                                    maxLength={constants.commonConstants.EMAIL_TO_SUPPLIER_SUBJECT_MAX_LENGTH}
                                    placeholder={getMessage('subjectPlaceholder') || ''}
                                    changeOnBlur
                                    allowTextFormatting={false}
                                    allowMention
                                    customMentionConfig={customMentionConfig}
                                    restrictNewLine
                                    readOnly={isDisabled}
                                    disabled={isDisabled}
                                    minHeight={30}
                                />
                            </Field>

                            <Field title={getMessage('bodyLabel')}>
                                <RichEditor
                                    dataQa={qaBody}
                                    value={body || ''}
                                    onChange={onChangeBody}
                                    invalid={hasErrors && !data.body}
                                    maxLength={constants.commonConstants.EMAIL_TO_SUPPLIER_BODY_MAX_LENGTH}
                                    placeholder={getMessage('bodyPlaceholder') || ''}
                                    changeOnBlur
                                    allowMention
                                    customMentionConfig={customMentionConfig}
                                    readOnly={isDisabled}
                                    disabled={isDisabled}
                                />
                            </Field>
                        </FieldsFullWidth>
                    </FieldsWrap>
                )}

                {data && isVisible && (
                    <FilesWrap title={getMessage('attachmentsLabel')}>
                        <>
                            {labels?.attachmentsNote && (
                                <NoteWrap>
                                    <AlertNotificationIcon />

                                    <FilesNote>{labels.attachmentsNote}</FilesNote>
                                </NoteWrap>
                            )}

                            <FilesList
                                readonly={isDisabled}
                                attachments={attachments}
                                newAttachments={attachmentsNew}
                                removeAttachment={onAttachRemove}
                                attachmentKind={AttachmentKind.EmailToSupplierAttachment}
                                customHandleClickOnAttachment={handleClickOnAttachment}
                            />
                        </>
                    </FilesWrap>
                )}
            </Content>
        </FormWrap>
    );
});

FormOld.displayName = 'FormOld';
